import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Send, X, Info, Mail, MessageSquare } from "lucide-react"
import { Database } from "@/integrations/supabase/types"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

type CollectionCode = Database["public"]["Tables"]["collection_codes"]["Row"]

interface HistoryTableProps {
  codes: CollectionCode[] | undefined
  onCancel: (code: string) => Promise<void>
  onSendNow: (code: string) => Promise<void>
  onShowDetails: (code: CollectionCode) => void
}

export function HistoryTable({ codes, onCancel, onSendNow, onShowDetails }: HistoryTableProps) {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'default'
      case 'sent':
        return 'secondary'
      case 'cancelled':
        return 'destructive'
      case 'used':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente'
      case 'sent':
        return 'Envoyé'
      case 'cancelled':
        return 'Annulé'
      case 'used':
        return 'Utilisé'
      default:
        return status
    }
  }

  return (
    <TooltipProvider>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Clé</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Date d'envoi</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes?.map((code) => (
              <TableRow key={code.id}>
                <TableCell className="font-medium">{code.code}</TableCell>
                <TableCell>{code.key_name}</TableCell>
                <TableCell>{code.client_name}</TableCell>
                <TableCell className="space-x-2">
                  {code.client_email && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Mail className="inline-block h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Email</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {code.client_phone && (
                    <Tooltip>
                      <TooltipTrigger>
                        <MessageSquare className="inline-block h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>SMS</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {code.sent_at ? new Date(code.sent_at).toLocaleString('fr-FR') : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(code.status)}>
                    {getStatusLabel(code.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onShowDetails(code)}
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Détails</p>
                    </TooltipContent>
                  </Tooltip>

                  {code.status !== 'cancelled' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onCancel(code.code)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Annuler</p>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {code.status === "pending" && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="default"
                          size="icon"
                          onClick={() => onSendNow(code.code)}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Envoyer maintenant</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  )
}